import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material';
import { red } from 'providers/theme/themePrimitives';

export const modernDangerButtonSx: SxProps<Theme> = (theme) => {
  const color = theme.palette.mode === 'dark' ? red[300] : red[600];

  return {
    color,
    borderColor: color,
    '&:hover': {
      color,
      borderColor: color,
      bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.12 : 0.06),
    },
  };
};

export const modernDashboardActionsSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  p: { xs: 1.5, md: 2 },
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2.5,
  bgcolor: 'background.default',
  boxShadow: (theme) => theme.shadows[1],
  '& .MuiAutocomplete-root': { width: '100%' },
};

export const modernDashboardContentSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  mt: 0.5,
  alignItems: 'stretch',
  '& > :not(.dashboard-section-heading)': {
    p: { xs: 1.5, md: 2 },
    minWidth: 0,
    maxWidth: '100%',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    bgcolor: 'background.paper',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.025)',
    transition: 'border-color 140ms ease, box-shadow 140ms ease',
    '&:focus-within': {
      borderColor: 'primary.main',
      boxShadow: (theme) =>
        `0 0 0 3px ${theme.palette.mode === 'dark' ? 'rgba(45, 152, 255, .14)' : 'rgba(0, 112, 204, .08)'}`,
    },
  },
  '& > .dashboard-section-heading': {
    mt: { xs: 1.5, md: 2.5 },
    mb: 0.25,
  },
  '& > .dashboard-section-heading:first-of-type': {
    mt: 0,
  },
  '& > .dashboard-section-notice': {
    p: '0 !important',
    border: 0,
    bgcolor: 'transparent',
    boxShadow: 'none',
  },
  '& .MuiFormLabel-root': {
    display: 'block',
    mb: 0.75,
    color: 'text.secondary',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.015em',
  },
  '& .MuiAccordion-root': {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    borderRadius: '8px !important',
    bgcolor: 'background.default',
  },
  '& .MuiAccordionDetails-root': {
    maxWidth: '100%',
    minWidth: 0,
    px: { xs: 1, md: 1.5 },
    pb: 1.5,
  },
  '& .MuiFormControlLabel-label.Mui-disabled': {
    color: 'text.primary',
    opacity: 1,
  },
  '& .MuiRadio-root.Mui-disabled': {
    color: 'text.secondary',
    opacity: 1,
  },
  '& .MuiRadio-root.Mui-checked.Mui-disabled': {
    color: 'primary.main',
  },
  '& .MuiSwitch-switchBase.Mui-disabled': {
    color: 'text.secondary',
    opacity: 1,
  },
  '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': {
    color: 'primary.main',
  },
  '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
    opacity: 0.4,
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& > :not(.dashboard-section-heading)': {
      transition: 'none',
    },
  },
};

export const modernDashboardSapSx: SxProps<Theme> = {
  display: { xs: 'grid', lg: 'flex' },
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(3, minmax(0, 1fr))' },
  width: '100%',
  gap: 2,
  '& > .MuiBox-root': {
    minWidth: 0,
    p: 1.5,
    borderColor: 'divider !important',
    borderRadius: 1.5,
    bgcolor: 'background.default',
  },
  '& .resize-handle': {
    display: { xs: 'none', lg: 'block' },
    width: '14px !important',
    right: '-7px !important',
    touchAction: 'none',
    backgroundColor: 'transparent',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 'calc(50% - 18px)',
      left: '6px',
      width: '2px',
      height: '36px',
      borderRadius: '999px',
      bgcolor: 'primary.main',
      opacity: 1,
      transition: 'width 120ms ease, opacity 120ms ease',
    },
    '&:hover::after, &:focus-visible::after': {
      width: '3px',
      opacity: 1,
    },
    '&:focus-visible': {
      outline: '3px solid',
      outlineColor: 'primary.main',
      outlineOffset: '1px',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& .resize-handle::after': { transition: 'none' },
  },
};
